import { ChangeDetectionStrategy, Component, computed, DestroyRef, effect, input, output, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { tap } from 'rxjs';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { NumberInputComponent, TextInputComponent } from '@examdojo/core/input';
import { SingleSelectComponent } from '@examdojo/core/select';
import { THEME_COLORS_WITH_SHADES } from '@examdojo/core/widget';
import { StyleEditorComponent, type StyleSectionDescriptor } from '@examdojo/core/widget';
import type { GeometryElementDef, GeometryElementDisplayConfig, GeometryDisplayConfig } from '../../geometrypad.model';
import {
  getElementMeta,
  getElementCategory,
  getElementPortGroups,
  getDisplayStyleMeta,
  getGeoStyleSections,
  computeSubpartLabelDefaults,
  computeSubpartVisibleDefaults,
  type EditorElementMeta,
  type ParamFieldDef,
  type AllElementType,
} from '../geometrypad-editor.model';

interface SelectOption {
  label: string;
  value: string;
}

interface ValueGroup {
  label: string;
  paths: Array<{ display: string; copyPath: string }>;
}

@Component({
  selector: 'dojo-geo-element-editor',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    MatSlideToggleModule,
    NumberInputComponent,
    TextInputComponent,
    SingleSelectComponent,
    StyleEditorComponent,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './element-editor.component.html',
})
export class ElementEditorComponent {
  constructor(private readonly destroyRef: DestroyRef) {
    effect(() => {
      this.elementDef();
      this.elementDisplay();
      this.syncFromInputs();
    });
  }

  // --- Inputs ---
  readonly elementId = input.required<string>();
  readonly elementDef = input.required<GeometryElementDef>();
  readonly elementDisplay = input<GeometryElementDisplayConfig>();
  readonly geometryConfig = input<GeometryDisplayConfig>();
  readonly allElementIds = input<string[]>([]);
  readonly shapeElementIds = input<string[]>([]);
  readonly shapeElements = input<Record<string, GeometryElementDef>>({});
  readonly allPortPaths = input<string[]>([]);

  // --- Outputs ---
  readonly defChange = output<Partial<GeometryElementDef>>();
  readonly displayChange = output<GeometryElementDisplayConfig>();

  // --- Pending display state (signal-based so the template can react immediately) ---
  private readonly pendingDisplay = signal<GeometryElementDisplayConfig | null>(null);

  /** Effective display: pending state takes priority over the input signal. */
  readonly effectiveDisplay = computed<GeometryElementDisplayConfig>(
    () => this.pendingDisplay() ?? this.elementDisplay() ?? {},
  );

  // --- Lazy form control maps ---
  private readonly textCtrls = new Map<string, FormControl<string>>();
  private readonly numberCtrls = new Map<string, FormControl<number | null>>();
  private readonly selectCtrls = new Map<string, FormControl<string>>();

  readonly colorOptions: SelectOption[] = [
    { label: 'Default', value: '' },
    ...THEME_COLORS_WITH_SHADES.map((c) => ({ label: c, value: c })),
  ];

  // --- Computed metadata ---
  readonly meta = computed<EditorElementMeta | undefined>(() => getElementMeta(this.elementDef().type));

  readonly category = computed(() => getElementCategory(this.elementDef().type as AllElementType));

  readonly paramFields = computed<readonly ParamFieldDef[]>(() => {
    const meta = this.meta();
    if (!meta) {
      return [];
    }
    return meta.paramsByFormat['_default'] ?? [];
  });

  readonly nameFields = computed<readonly string[]>(() => {
    const meta = this.meta();
    return meta?.nameFields ?? [];
  });

  readonly hasNames = computed(() => {
    const meta = this.meta();
    return meta?.hasNames === true;
  });

  readonly requiresParent = computed(() => {
    const meta = this.meta();
    return meta?.requiresParent === true;
  });

  readonly validParentIds = computed<string[]>(() => {
    const meta = this.meta();
    if (!meta?.validParentTypes) {
      return this.shapeElementIds();
    }
    const validTypes = new Set(meta.validParentTypes);
    const elements = this.shapeElements();
    return Object.entries(elements)
      .filter(([, def]) => validTypes.has(def.type))
      .map(([id]) => id);
  });

  readonly parentOptions = computed<SelectOption[]>(() => {
    return this.validParentIds().map((id) => ({ label: id, value: id }));
  });

  readonly displayStyleKeys = computed(() => {
    return getDisplayStyleMeta(this.elementDef().type).keys;
  });

  readonly styleSections = computed<readonly StyleSectionDescriptor[]>(() => {
    return getGeoStyleSections(this.elementDef().type);
  });

  readonly labelDefaults = computed<Record<string, boolean> | null>(() => {
    const def = this.elementDef();
    const params = (def as unknown as Record<string, unknown>)['params'] as Record<string, unknown> | undefined;
    return computeSubpartLabelDefaults(def.type, params, this.geometryConfig());
  });

  readonly visibleDefaults = computed<Record<string, boolean> | null>(() => {
    const def = this.elementDef();
    const params = (def as unknown as Record<string, unknown>)['params'] as Record<string, unknown> | undefined;
    return computeSubpartVisibleDefaults(def.type, params, this.geometryConfig());
  });

  readonly isPolygonType = computed(() => this.elementDef().type === 'Polygon');

  readonly hasLabelConfig = computed(() => this.elementDef().type === 'LengthAnnotation');

  readonly showGrabHandleValue = computed(() => {
    const display = this.effectiveDisplay();
    return (display?.point as Record<string, unknown> | undefined)?.['showGrabHandle'] === true;
  });

  readonly portGroups = computed<ValueGroup[]>(() => {
    const def = this.elementDef();
    const id = this.elementId();
    const params = (def as unknown as Record<string, unknown>)['params'] as Record<string, unknown> | undefined;
    const groups = getElementPortGroups(def.type, params);
    return groups
      .filter((g) => g.ports.length > 0)
      .map((g) => ({
        label: g.label,
        paths: g.ports.map((port) => ({
          display: `${id}.${port}`,
          copyPath: `${id}.${port}`,
        })),
      }));
  });

  readonly portRefOptions = computed<SelectOption[]>(() => [
    { label: 'None', value: '' },
    ...this.allPortPaths().map((path) => ({ label: path, value: path })),
  ]);

  copyToClipboard(path: string): void {
    navigator.clipboard.writeText(path);
  }

  getParamSelectOptions(field: ParamFieldDef): SelectOption[] {
    if (!field.options) {
      return [];
    }
    return field.options.map((o) => ({ label: o, value: o }));
  }

  // --- Form control getters ---

  getTextCtrl(key: string): FormControl<string> {
    if (!this.textCtrls.has(key)) {
      const ctrl = new FormControl<string>('', { nonNullable: true });
      this.textCtrls.set(key, ctrl);
      ctrl.valueChanges
        .pipe(
          tap((val) => this.onFieldChange(key, val)),
          takeUntilDestroyed(this.destroyRef),
        )
        .subscribe();
    }
    return this.textCtrls.get(key)!;
  }

  getNumberCtrl(key: string): FormControl<number | null> {
    if (!this.numberCtrls.has(key)) {
      const ctrl = new FormControl<number | null>(null);
      this.numberCtrls.set(key, ctrl);
      ctrl.valueChanges
        .pipe(
          tap((val) => this.onFieldChange(key, val)),
          takeUntilDestroyed(this.destroyRef),
        )
        .subscribe();
    }
    return this.numberCtrls.get(key)!;
  }

  getSelectCtrl(key: string): FormControl<string> {
    if (!this.selectCtrls.has(key)) {
      this.selectCtrls.set(key, new FormControl<string>('', { nonNullable: true }));
    }
    return this.selectCtrls.get(key)!;
  }

  // --- Change handlers ---

  onParentChange(parentId: unknown): void {
    const def = this.elementDef();
    const params = { ...(((def as unknown as Record<string, unknown>)['params'] as Record<string, unknown>) ?? {}) };
    params['of'] = parentId;
    this.defChange.emit({ params } as unknown as Partial<GeometryElementDef>);
  }

  onNameChange(field: string, value: string): void {
    const def = this.elementDef();
    const names = { ...(((def as unknown as Record<string, unknown>)['names'] as Record<string, string>) ?? {}) };
    if (value) {
      names[field] = value;
    } else {
      delete names[field];
    }
    this.defChange.emit({ names } as unknown as Partial<GeometryElementDef>);
  }

  onDisplayStyleChange(styleKey: string, property: string, value: unknown): void {
    const base = this.pendingDisplay() ?? this.elementDisplay() ?? {};
    const currentStyle = ((base as Record<string, unknown>)[styleKey] as Record<string, unknown>) ?? {};
    const cleanValue = value === '' ? undefined : value;
    const newDisplay = {
      ...base,
      [styleKey]: { ...currentStyle, [property]: cleanValue },
    };
    this.pendingDisplay.set(newDisplay);
    this.displayChange.emit(newDisplay);
  }

  onStyleEditorChange(newStyleData: Record<string, unknown>): void {
    const base = this.pendingDisplay() ?? this.elementDisplay() ?? {};
    const newDisplay = { ...base, ...newStyleData };
    this.pendingDisplay.set(newDisplay);
    this.displayChange.emit(newDisplay);
  }

  onPolygonVerticesChange(verticesJson: string): void {
    try {
      const vertices = JSON.parse(verticesJson);
      if (Array.isArray(vertices)) {
        this.defChange.emit({ params: { vertices } } as unknown as Partial<GeometryElementDef>);
      }
    } catch {
      // Invalid JSON — ignore
    }
  }

  getPolygonVerticesJson(): string {
    const def = this.elementDef();
    const params = (def as unknown as Record<string, unknown>)['params'] as Record<string, unknown>;
    return JSON.stringify(params?.['vertices'] ?? [], null, 2);
  }

  // --- Private ---

  onFieldChange(key: string, value: unknown): void {
    // Determine whether this is a param field or something else
    const paramPrefix = 'param.';
    const namePrefix = 'name.';
    const displayPrefix = 'display.';

    if (key.startsWith(paramPrefix)) {
      const paramKey = key.slice(paramPrefix.length);
      const def = this.elementDef();
      const params = { ...(((def as unknown as Record<string, unknown>)['params'] as Record<string, unknown>) ?? {}) };

      // Handle coord-or-ref: try to parse as JSON {x, y}, otherwise keep as string
      const field = this.paramFields().find((f) => f.key === paramKey);
      if (field?.type === 'coord-or-ref' && typeof value === 'string') {
        try {
          const parsed = JSON.parse(value);
          if (parsed && typeof parsed === 'object' && 'x' in parsed && 'y' in parsed) {
            params[paramKey] = parsed;
          } else {
            params[paramKey] = value;
          }
        } catch {
          params[paramKey] = value || undefined;
        }
      } else if (field?.type === 'value-expr' && typeof value === 'string') {
        // Value expression: try to parse as number, otherwise keep as string
        const num = Number(value);
        params[paramKey] = isNaN(num) ? value : num;
      } else if (field?.type === 'tuple-number' && typeof value === 'string') {
        try {
          const parsed = JSON.parse(value);
          if (Array.isArray(parsed)) {
            params[paramKey] = parsed;
          }
        } catch {
          // Ignore
        }
      } else if (field?.type === 'port-ref') {
        params[paramKey] = value || undefined;
      } else {
        params[paramKey] = value;
      }

      this.defChange.emit({ params } as unknown as Partial<GeometryElementDef>);
    } else if (key.startsWith(namePrefix)) {
      this.onNameChange(key.slice(namePrefix.length), value as string);
    } else if (key.startsWith(displayPrefix)) {
      const parts = key.slice(displayPrefix.length).split('.');
      if (parts.length === 2) {
        this.onDisplayStyleChange(parts[0], parts[1], value);
      }
    } else if (key === 'polygonVertices') {
      this.onPolygonVerticesChange(value as string);
    }
  }

  private syncFromInputs(): void {
    this.pendingDisplay.set(null);
    const _display = this.elementDisplay() ?? {};
    const def = this.elementDef();
    const params = ((def as unknown as Record<string, unknown>)['params'] as Record<string, unknown>) ?? {};
    const names = ((def as unknown as Record<string, unknown>)['names'] as Record<string, string>) ?? {};

    // Sync parent
    if (params['of']) {
      this.getSelectCtrl('parent').setValue(params['of'] as string, { emitEvent: false });
    }

    // Sync param fields
    for (const field of this.paramFields()) {
      const val = params[field.key];
      if (field.type === 'number') {
        this.getNumberCtrl(`param.${field.key}`).setValue((val as number) ?? null, { emitEvent: false });
      } else if (field.type === 'value-expr' || field.type === 'string') {
        const strVal = val !== undefined && val !== null ? String(val) : '';
        this.getTextCtrl(`param.${field.key}`).setValue(strVal, { emitEvent: false });
      } else if (field.type === 'port-ref') {
        const strVal = val !== undefined && val !== null ? String(val) : '';
        this.getSelectCtrl(`param.${field.key}`).setValue(strVal, { emitEvent: false });
      } else if (field.type === 'coord-or-ref') {
        const strVal =
          typeof val === 'object' ? JSON.stringify(val) : val !== undefined && val !== null ? String(val) : '';
        this.getTextCtrl(`param.${field.key}`).setValue(strVal, { emitEvent: false });
      } else if (field.type === 'tuple-number') {
        const strVal = Array.isArray(val) ? JSON.stringify(val) : '';
        this.getTextCtrl(`param.${field.key}`).setValue(strVal, { emitEvent: false });
      } else if (field.type === 'select') {
        this.getSelectCtrl(`param.${field.key}`).setValue((val as string) ?? '', { emitEvent: false });
      }
    }

    // Sync names
    const meta = this.meta();
    if (meta?.hasNames && meta.nameFields) {
      for (const nf of meta.nameFields) {
        this.getTextCtrl(`name.${nf}`).setValue(names[nf] ?? '', { emitEvent: false });
      }
    }

    // Display style controls are now handled by the StyleEditorComponent via its inputs
  }
}
